import React, { useState, useRef, useEffect } from "react";
import { Container, Row, Col, Card, Button, Form, Alert, Spinner, ProgressBar, Breadcrumb } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileUpload, faFileAlt, faSearch, faSave, faList, faDownload, faTrashAlt, faExclamationTriangle, faPlus, faLanguage } from "@fortawesome/free-solid-svg-icons";
import { analyzeContract, getSavedAnalyses, saveAnalysis, deleteAnalysis } from "../../../service/workflow-api";
import "../common.css";
import SavedAnalysisModal from "./SavedAnalysisModal";
import AnalysisDetails from "./AnalysisDetails";
import AnalysisList from "./AnalysisList";
import { supportedLanguages } from "../contracts-maker/data";

const ContractAnalyzer = () => {
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [progress, setProgress] = useState(0);
  const [showSavedModal, setShowSavedModal] = useState(false);
  const [viewMode, setViewMode] = useState("upload"); // "upload", "results", "saved"
  const [savedAnalyses, setSavedAnalyses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState("en");
  const fileInputRef = useRef(null);

  useEffect(() => {
    document.title = "BeatAI - Contract Analyzer";
  }, []);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Check file size (max 20MB)
      if (selectedFile.size > 20 * 1024 * 1024) {
        setError("File size exceeds the maximum limit of 20MB");
        setFile(null);
        setFileName("");
        return;
      }

      // Check file type
      const fileType = selectedFile.type;
      if (
        fileType !== "application/pdf" &&
        fileType !== "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      ) {
        setError("Only PDF and DOCX (Microsoft) files are supported");
        setFile(null);
        setFileName("");
        return;
      }

      setFile(selectedFile);
      setFileName(selectedFile.name);
      setError(null);
    }
  };

  const handleAnalyze = async () => {
    if (!file) {
      setError("Please select a file to analyze");
      return;
    }

    setIsAnalyzing(true);
    setProgress(0);
    setError(null);

    try {
      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setProgress((prev) => {
          const newProgress = prev + Math.random() * 15;
          return newProgress > 90 ? 90 : newProgress;
        });
      }, 1000);

      // Pass the selected language to the API
      const result = await analyzeContract(file, selectedLanguage);
      clearInterval(progressInterval);
      setProgress(100);
      setAnalysis(result);
      // If the filename is already set from the file upload, keep it
      // otherwise use the file_name from the API response
      if (!fileName && result.file_name) {
        setFileName(result.file_name);
      }
      setViewMode("results");
    } catch (err) {
      setError(`Analysis failed: ${err.message || "Unknown error"}`);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleSave = async (name, description) => {
    if (!analysis) return;

    try {
      setIsLoading(true);
      await saveAnalysis({
        name,
        description,
        originalFileName: fileName,
        analysis
      });
      setShowSavedModal(false);
      loadSavedAnalyses();
    } catch (error) {
      setError(`Failed to save analysis: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const loadSavedAnalyses = async () => {
    try {
      setIsLoading(true);
      const analyses = await getSavedAnalyses();
      setSavedAnalyses(analyses);
    } catch (error) {
      setError(`Failed to load saved analyses: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewSaved = () => {
    loadSavedAnalyses();
    setViewMode("saved");
  };

  const handleDeleteAnalysis = async (id) => {
    try {
      await deleteAnalysis(id);
      loadSavedAnalyses(); // Refresh the list
    } catch (error) {
      setError(`Failed to delete analysis: ${error.message}`);
    }
  };

  const handleViewAnalysis = (analysis) => {
    setSelectedAnalysis(analysis);
    // Use the file_name from the analysis if available
    if (analysis.file_name) {
      setFileName(analysis.file_name);
    }
    setViewMode("results");
  };

  const handleBack = () => {
    setViewMode("upload");
    setFile(null);
    setFileName("");
    setAnalysis(null);
    setSelectedAnalysis(null);
    setError(null);
  };

  return (
    <>
      <Breadcrumb className="dark-breadcrumb mb-4" style={{ color: "white" }}>
        <Breadcrumb.Item href="/ai-workflows">AI Workflows</Breadcrumb.Item>
        <Breadcrumb.Item active>Contract Analyzer</Breadcrumb.Item>
      </Breadcrumb>

      <Container fluid className="budget-planner-container py-4 min-vh-100">
        <Row className="justify-content-center">
          <Col lg={10}>
            <Card className="dark-main-card text-light mb-4">
              <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <Card.Title className="text-primary" as="h4" style={{ fontWeight: 300, color: "#d9d9d9", textShadow: "rgba(79, 209, 197, 0.3) 0px 0px 10px;" }}>
                      Contract Analyzer & Summarizer
                  </Card.Title>
                  <div>
                    {viewMode !== "upload" && (
                      <Button
                        variant="outline-primary"
                        className="dark-outline-button me-2"
                        onClick={handleBack}
                      >
                        Back to Upload
                      </Button>
                    )}
                    <Button
                      variant="outline-primary"
                      size="md"
                      className="dark-outline-button"
                      onClick={handleViewSaved}
                    >
                      <FontAwesomeIcon icon={faList} className="me-2" />
                      History
                    </Button>
                  </div>
                </div>

                <div className="mb-4">
                  <Form.Group controlId="languageSelector" className="d-flex align-items-center">
                    <Form.Label className="text-light me-2 mb-0">Output </Form.Label>
                    <Form.Select
                      value={selectedLanguage}
                      onChange={(e) => setSelectedLanguage(e.target.value)}
                      style={{ 
                        width: 'auto', 
                        backgroundColor: '#212529', 
                        color: 'white',
                        padding: '0.375rem 2.25rem 0.375rem 0.75rem'
                      }}
                    >
                      {supportedLanguages.map(lang => (
                        <option
                          key={lang.id}
                          value={lang.id}
                          style={{ 
                            backgroundColor: '#212529', 
                            color: 'white',
                            padding: '4px 8px'
                          }}
                        >
                          {lang.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>

                {error && (
                  <Alert
                    variant="danger"
                    className="mb-4"
                    style={{
                      background: "rgba(220, 53, 69, 0.1)",
                      border: "1px solid rgba(220, 53, 69, 0.2)",
                      color: "#ffffff",
                      backdropFilter: "blur(10px)",
                      WebkitBackdropFilter: "blur(10px)",
                      borderRadius: "8px",
                    }}
                    dismissible
                    onClose={() => setError(null)}
                  >
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    {error}
                  </Alert>
                )}

                {viewMode === "upload" && (
                  <div className="mb-4">
                    {!fileName ? (
                      <div
                        className="text-center p-5 mb-4"
                        style={{
                          background: "rgba(255, 255, 255, 0.05)",
                          border: "1px dashed rgba(255, 255, 255, 0.2)",
                          borderRadius: "12px",
                          cursor: "pointer",
                          transition: "all 0.3s ease",
                        }}
                        onClick={() => fileInputRef.current && fileInputRef.current.click()}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)";
                          e.currentTarget.style.border = "1px dashed rgba(255, 255, 255, 0.4)";
                        }}
                        onDragLeave={(e) => {
                          e.preventDefault();
                          e.currentTarget.style.background = "rgba(255, 255, 255, 0.05)";
                          e.currentTarget.style.border = "1px dashed rgba(255, 255, 255, 0.2)";
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.currentTarget.style.background = "rgba(255, 255, 255, 0.05)";
                          e.currentTarget.style.border = "1px dashed rgba(255, 255, 255, 0.2)";
                          
                          const files = e.dataTransfer.files;
                          if (files.length) {
                            const droppedFile = files[0];
                            
                            // Check file size (max 20MB)
                            if (droppedFile.size > 20 * 1024 * 1024) {
                              setError("File size exceeds the maximum limit of 20MB");
                              return;
                            }

                            // Check file type
                            const fileType = droppedFile.type;
                            if (
                              fileType !== "application/pdf" &&
                              fileType !== "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                            ) {
                              setError("Only PDF and DOCX files are supported");
                              return;
                            }

                            setFile(droppedFile);
                            setFileName(droppedFile.name);
                            setError(null);
                          }
                        }}
                      >
                        <FontAwesomeIcon
                          icon={faFileUpload}
                          size="3x"
                          style={{ color: "rgba(255, 255, 255, 0.5)", marginBottom: "20px" }}
                        />
                        <h4 className="mb-3" style={{
                          color: "#d9d9d9"
                        }}>Drag & Drop Your Contract File Here</h4>
                        <p className="text-muted mb-3">
                          or click to browse your files
                        </p>
                        <p className="text-muted small mb-0">
                          Supported formats: PDF, DOCX (Max: 20MB)
                        </p>
                      </div>
                    ) : (
                      <Card className="mb-4" style={{ 
                        background: "rgba(255, 255, 255, 0.08)",
                        borderRadius: "12px",
                        border: "none"
                      }}>
                        <Card.Body>
                          <div className="d-flex justify-content-between align-items-center">
                            <div className="d-flex align-items-center">
                              <div className="me-3 p-3" style={{ 
                                backgroundColor: "rgba(79, 209, 197, 0.1)",
                                borderRadius: "8px"
                              }}>
                                <FontAwesomeIcon
                                  icon={faFileAlt}
                                  size="lg"
                                  style={{ color: "var(--accent-blue)" }}
                                />
                              </div>
                              <div>
                                <h5 className="mb-1 text-light">{fileName}</h5>
                                <p className="mb-0 text-muted">
                                  {(file.size / 1024 / 1024).toFixed(2)} MB - {file.type.split('/').pop().toUpperCase()}
                                </p>
                              </div>
                            </div>
                            <div>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                className="rounded-circle p-2"
                                style={{ 
                                  width: "40px", 
                                  height: "40px", 
                                  display: "flex", 
                                  alignItems: "center", 
                                  justifyContent: "center" 
                                }}
                                onClick={() => {
                                  setFile(null);
                                  setFileName("");
                                }}
                                title="Remove file"
                              >
                                <FontAwesomeIcon icon={faTrashAlt} />
                              </Button>
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    )}

                    <Form.Control
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileChange}
                      accept=".pdf,.docx"
                      style={{ display: "none" }}
                    />

                    {fileName && (
                      <div className="d-flex justify-content-center my-3">
                        <Button
                          variant="outline-primary"
                          className="me-2"
                          onClick={() => fileInputRef.current && fileInputRef.current.click()}
                        >
                          <FontAwesomeIcon icon={faPlus} className="me-2" />
                          Change File
                        </Button>
                      </div>
                    )}

                    {isAnalyzing ? (
                      <>
                        <div className="text-center my-4">
                          <Spinner animation="border" variant="primary" />
                          <p className="mt-3 text-light">Analyzing your contract...</p>
                        </div>
                        <ProgressBar 
                          now={progress} 
                          label={`${Math.round(progress)}%`} 
                          animated 
                          style={{ 
                            height: "10px", 
                            backgroundColor: "rgba(255, 255, 255, 0.1)"
                          }} 
                        />
                      </>
                    ) : (
                      <div className="text-center mt-4">
                        <Button
                          variant="primary"
                          className="dark-primary-button"
                          size="md"
                          onClick={handleAnalyze}
                          disabled={!file}
                        >
                          <FontAwesomeIcon icon={faSearch} className="me-2" />
                          Analyze Contract
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {viewMode === "results" && (
                  <AnalysisDetails 
                    analysis={selectedAnalysis || analysis}
                    fileName={fileName || (selectedAnalysis && selectedAnalysis.originalFileName)}
                    onSave={() => setShowSavedModal(true)}
                    onBack={handleBack}
                  />
                )}

                {viewMode === "saved" && (
                  <AnalysisList
                    analyses={savedAnalyses}
                    isLoading={isLoading}
                    onView={handleViewAnalysis}
                    onDelete={handleDeleteAnalysis}
                    onBack={handleBack}
                  />
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      <SavedAnalysisModal
        show={showSavedModal}
        onHide={() => setShowSavedModal(false)}
        onSave={handleSave}
        isLoading={isLoading}
      />
    </>
  );
};

export default ContractAnalyzer;